import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useConfig } from '../context/ConfigContext';

// Emoji options for icon picker
const EMOJI_OPTIONS = [
  '💰', '💵', '💲', '🏠', '🏡', '🌳', '🌲', '🏞️', '📋', '📄', '📝', '✅', '✔️', '🎯', '🏆', '🥇',
  '⭐', '🌟', '💎', '🔑', '🔒', '🛡️', '👍', '👌', '🤝', '💪', '❤️', '😊', '📞', '📧', '🚀', '⚡',
  '🔥', '💡', '🎉', '📈', '📊', '🗓️', '⏰', '🕐', '🌍', '🌎', '🗺️', '🚗', '✈️', '🏦', '💳', '🧾', '📦', '🎁'
];

// Emoji Picker Component
function EmojiPicker({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={pickerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '8px 10px',
          border: '1px solid var(--color-border)',
          borderRadius: '6px',
          fontSize: '24px',
          backgroundColor: 'var(--color-surface)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '44px'
        }}
      >
        {value || '⭐'}
      </button>
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 1000,
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          padding: '10px',
          marginTop: '4px',
          width: '280px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: '4px'
          }}>
            {EMOJI_OPTIONS.map((emoji, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  onChange(emoji);
                  setIsOpen(false);
                }}
                style={{
                  padding: '6px',
                  fontSize: '20px',
                  border: value === emoji ? '2px solid var(--color-primary)' : '1px solid transparent',
                  borderRadius: '6px',
                  backgroundColor: value === emoji ? '#e8f4ea' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  if (value !== emoji) {
                    e.target.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (value !== emoji) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Default content for Home page
//
// This is what the editor shows an admin who has never saved page content, so it
// is the copy they are most likely to publish unchanged -- which is exactly why
// it must not carry another business's figures. It used to open with "$99 Down
// Payment" and "1-5 years", pre-filling every new tenant's homepage with terms
// they had never agreed to and making the wrong copy the path of least
// resistance.
//
// Byte-identical to DEFAULT_HOME_CONTENT in client/src/pages/Home.js and in
// server/server.js. Keep all three in step: the editor must show the same text
// the public page renders and the API hands back, or an admin edits one thing
// and visitors see another.
const DEFAULT_HOME_CONTENT = {
  whyChooseUs: [
    { id: 1, icon: '💰', title: 'Low Down Payment', description: 'Flexible down payment options make land ownership accessible. Choose the plan that fits your budget.' },
    { id: 2, icon: '📋', title: 'Flexible Terms', description: 'Choose the payment term that works for you. No credit checks, no bank hassles.' },
    { id: 3, icon: '🎯', title: 'Simple Process', description: "Browse properties, choose your terms, and start making payments online. It's that easy." },
    { id: 4, icon: '🏆', title: 'No Hidden Fees', description: 'Transparent pricing with no prepayment penalties. Pay off early and save on interest.' }
  ],
  howItWorks: [
    { id: 1, step: 1, title: 'Browse Properties', description: 'Explore our available land parcels and find the perfect property for your needs.' },
    { id: 2, step: 2, title: 'Choose Your Terms', description: 'Use our calculator to select your down payment amount and loan term.' },
    { id: 3, step: 3, title: 'Complete Purchase', description: 'Sign up, make your down payment securely online, and receive your loan details.' },
    { id: 4, step: 4, title: 'Own Your Land', description: 'Make monthly payments through your dashboard and watch your equity grow.' }
  ]
};

// Default content for About page
//
// Same rules as DEFAULT_HOME_CONTENT above, and `timeline` is empty for the
// reason spelled out in client/src/pages/AboutUs.js: a founding year is a
// factual claim about a specific company and has no neutral phrasing. The editor
// renders an empty array as "No items yet. Click Add to create one", which is
// the correct prompt -- the admin supplies their own history or the Our Story
// section stays off.
const DEFAULT_ABOUT_CONTENT = {
  heroSubtitle: 'Making land ownership accessible to everyone through flexible financing and transparent service.',
  missionStatement: "We believe land ownership shouldn't be limited to those with perfect credit or large cash reserves. Our mission is to help everyday Americans achieve their dream of owning land through accessible financing options and exceptional customer service.",
  values: [
    { id: 1, icon: '🤝', title: 'Transparency', description: 'No hidden fees, no surprises. We believe in clear communication and honest business practices.' },
    { id: 2, icon: '💪', title: 'Accessibility', description: 'We work with customers regardless of credit score. Everyone deserves a chance at land ownership.' },
    { id: 3, icon: '🎯', title: 'Simplicity', description: 'Our process is straightforward. Browse, choose, and purchase. All online with no complicated paperwork.' },
    { id: 4, icon: '🌟', title: 'Support', description: "We're here for you throughout your land ownership journey, from purchase to payoff." }
  ],
  timeline: [],
  offerings: [
    { id: 1, icon: '🏞️', title: 'Quality Land', description: 'We hand-select every property in our portfolio, ensuring it offers real value.' },
    { id: 2, icon: '💳', title: 'Flexible Financing', description: 'Multiple down payment options and loan terms to fit your budget.' },
    { id: 3, icon: '📱', title: 'Online Dashboard', description: 'Manage your loan, make payments, view documents, and track your progress online 24/7.' },
    { id: 4, icon: '✅', title: 'No Credit Checks', description: "We don't run credit checks. Approval is based on your ability to make payments." },
    { id: 5, icon: '📄', title: 'Clear Contracts', description: "Easy-to-understand contracts for deed with no legal jargon." }
  ]
};

function PageContentSettings() {
  const navigate = useNavigate();
  const { isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('home');
  const [hasChanges, setHasChanges] = useState(false);

  // Content state
  const [homeContent, setHomeContent] = useState(DEFAULT_HOME_CONTENT);
  const [aboutContent, setAboutContent] = useState(DEFAULT_ABOUT_CONTENT);
  const [originalHomeContent, setOriginalHomeContent] = useState(DEFAULT_HOME_CONTENT);
  const [originalAboutContent, setOriginalAboutContent] = useState(DEFAULT_ABOUT_CONTENT);

  // Editing states
  const [editingWhyChooseId, setEditingWhyChooseId] = useState(null);
  const [editingHowItWorksId, setEditingHowItWorksId] = useState(null);
  const [editingValueId, setEditingValueId] = useState(null);
  const [editingTimelineId, setEditingTimelineId] = useState(null);
  const [editingOfferingId, setEditingOfferingId] = useState(null);

  useEffect(() => {
    fetchPageContent();
  }, []);

  useEffect(() => {
    const homeChanged = JSON.stringify(homeContent) !== JSON.stringify(originalHomeContent);
    const aboutChanged = JSON.stringify(aboutContent) !== JSON.stringify(originalAboutContent);
    setHasChanges(homeChanged || aboutChanged);
  }, [homeContent, aboutContent, originalHomeContent, originalAboutContent]);

  const fetchPageContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/page-content', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.homeContent) {
        setHomeContent(response.data.homeContent);
        setOriginalHomeContent(response.data.homeContent);
      }
      if (response.data.aboutContent) {
        setAboutContent(response.data.aboutContent);
        setOriginalAboutContent(response.data.aboutContent);
      }
      setError('');
    } catch (err) {
      console.error('Failed to fetch page content:', err);
      // Use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      await api.post('/admin/page-content',
        { homeContent, aboutContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOriginalHomeContent(homeContent);
      setOriginalAboutContent(aboutContent);
      setHasChanges(false);
      setSuccess('Page content saved successfully!');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to save page content:', err);
      setError(err.response?.data?.error || 'Failed to save page content');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setHomeContent(originalHomeContent);
      setAboutContent(originalAboutContent);
    }
    navigate('/admin/settings-hub');
  };

  // Generic array item handlers
  const handleMoveUp = (array, setArray, index) => {
    if (index === 0) return;
    const newArray = [...array];
    [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
    setArray(newArray);
  };

  const handleMoveDown = (array, setArray, index) => {
    if (index === array.length - 1) return;
    const newArray = [...array];
    [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
    setArray(newArray);
  };

  const handleDelete = (array, setArray, id, setEditingId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setArray(array.filter(item => item.id !== id));
      if (setEditingId) setEditingId(null);
    }
  };

  const handleAdd = (array, setArray, defaultItem, setEditingId) => {
    const newId = Math.max(...array.map(item => item.id), 0) + 1;
    const newItem = { ...defaultItem, id: newId };
    setArray([...array, newItem]);
    setEditingId(newId);
  };

  // Render array item editor
  const renderArrayEditor = (
    items,
    setItems,
    editingId,
    setEditingId,
    fields,
    defaultItem,
    title,
    contentKey,
    contentSetter
  ) => {
    const updateItem = (id, field, value) => {
      const newItems = items.map(item => item.id === id ? { ...item, [field]: value } : item);
      if (contentKey === 'whyChooseUs' || contentKey === 'howItWorks') {
        setHomeContent(prev => ({ ...prev, [contentKey]: newItems }));
      } else {
        setAboutContent(prev => ({ ...prev, [contentKey]: newItems }));
      }
    };

    return (
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0, color: 'var(--color-brand-text)' }}>{title} ({items.length})</h3>
          <button
            onClick={() => handleAdd(items, (newItems) => {
              if (contentKey === 'whyChooseUs' || contentKey === 'howItWorks') {
                setHomeContent(prev => ({ ...prev, [contentKey]: newItems }));
              } else {
                setAboutContent(prev => ({ ...prev, [contentKey]: newItems }));
              }
            }, defaultItem, setEditingId)}
            className="btn btn-primary"
            style={{ padding: '6px 12px', fontSize: '14px' }}
          >
            + Add
          </button>
        </div>

        {items.length === 0 ? (
          <div style={{ padding: '20px', backgroundColor: 'var(--color-surface-sunken)', borderRadius: '8px', textAlign: 'center', color: 'var(--color-text-light)' }}>
            No items yet. Click "Add" to create one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {items.map((item, index) => (
              <div
                key={item.id}
                style={{
                  border: editingId === item.id ? '2px solid var(--color-primary)' : '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '12px',
                  backgroundColor: editingId === item.id ? '#f8f9ff' : '#fff'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: editingId === item.id ? '12px' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                    {item.icon && <span style={{ fontSize: '20px' }}>{item.icon}</span>}
                    {item.step && (
                      <span style={{
                        backgroundColor: 'var(--color-primary)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {item.step}
                      </span>
                    )}
                    {item.year && (
                      <span style={{
                        backgroundColor: 'var(--color-secondary)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {item.year}
                      </span>
                    )}
                    {editingId !== item.id && (
                      <div>
                        <strong>{item.title}</strong>
                        <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--color-text-light)' }}>{item.description?.substring(0, 80)}...</p>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => handleMoveUp(items, (newItems) => {
                      if (contentKey === 'whyChooseUs' || contentKey === 'howItWorks') {
                        setHomeContent(prev => ({ ...prev, [contentKey]: newItems }));
                      } else {
                        setAboutContent(prev => ({ ...prev, [contentKey]: newItems }));
                      }
                    }, index)} disabled={index === 0} style={{ padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: index === 0 ? '#f5f5f5' : 'white', cursor: index === 0 ? 'not-allowed' : 'pointer' }}>↑</button>
                    <button onClick={() => handleMoveDown(items, (newItems) => {
                      if (contentKey === 'whyChooseUs' || contentKey === 'howItWorks') {
                        setHomeContent(prev => ({ ...prev, [contentKey]: newItems }));
                      } else {
                        setAboutContent(prev => ({ ...prev, [contentKey]: newItems }));
                      }
                    }, index)} disabled={index === items.length - 1} style={{ padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: index === items.length - 1 ? '#f5f5f5' : 'white', cursor: index === items.length - 1 ? 'not-allowed' : 'pointer' }}>↓</button>
                    <button onClick={() => setEditingId(editingId === item.id ? null : item.id)} style={{ padding: '4px 10px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: editingId === item.id ? 'var(--color-primary)' : 'white', color: editingId === item.id ? 'white' : '#333', cursor: 'pointer' }}>{editingId === item.id ? 'Done' : 'Edit'}</button>
                    <button onClick={() => handleDelete(items, (newItems) => {
                      if (contentKey === 'whyChooseUs' || contentKey === 'howItWorks') {
                        setHomeContent(prev => ({ ...prev, [contentKey]: newItems }));
                      } else {
                        setAboutContent(prev => ({ ...prev, [contentKey]: newItems }));
                      }
                    }, item.id, setEditingId)} style={{ padding: '4px 10px', border: '1px solid var(--color-error-border)', borderRadius: '4px', backgroundColor: 'var(--color-surface)', color: 'var(--color-error)', cursor: 'pointer' }}>Delete</button>
                  </div>
                </div>

                {editingId === item.id && (
                  <div style={{ display: 'grid', gridTemplateColumns: fields.some(f => f.key === 'icon' || f.key === 'year' || f.key === 'step') ? '100px 1fr' : '1fr', gap: '12px' }}>
                    {fields.map(field => (
                      <div key={field.key} style={{ gridColumn: field.fullWidth ? '1 / -1' : 'auto' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>{field.label}</label>
                        {field.type === 'textarea' ? (
                          <textarea
                            value={item[field.key] || ''}
                            onChange={(e) => updateItem(item.id, field.key, e.target.value)}
                            placeholder={field.placeholder}
                            rows={3}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
                          />
                        ) : field.type === 'emoji' ? (
                          <EmojiPicker
                            value={item[field.key] || ''}
                            onChange={(emoji) => updateItem(item.id, field.key, emoji)}
                          />
                        ) : (
                          <input
                            type="text"
                            value={item[field.key] || ''}
                            onChange={(e) => updateItem(item.id, field.key, e.target.value)}
                            placeholder={field.placeholder}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box' }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-dashboard-header">
          <div className="admin-dashboard-header-text">
            <h1 className="admin-page-title">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <div className="admin-hub-header">
        <button onClick={handleCancel} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <h1 className="admin-page-title">Page Content Settings</h1>
        <p className="admin-hub-subtitle">Edit content for Home and About pages</p>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          backgroundColor: 'var(--color-success-bg)',
          border: '1px solid var(--color-success-border)',
          color: 'var(--color-success-text)',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '20px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>✓</span> {success}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid var(--color-border)' }}>
        <button
          onClick={() => setActiveTab('home')}
          style={{
            padding: '12px 24px',
            border: 'none',
            backgroundColor: activeTab === 'home' ? 'var(--color-primary)' : 'transparent',
            color: activeTab === 'home' ? 'white' : '#333',
            cursor: 'pointer',
            fontWeight: '600',
            borderRadius: '8px 8px 0 0',
            marginBottom: '-2px',
            borderBottom: activeTab === 'home' ? '2px solid var(--color-primary)' : '2px solid transparent'
          }}
        >
          🏠 Home Page
        </button>
        <button
          onClick={() => setActiveTab('about')}
          style={{
            padding: '12px 24px',
            border: 'none',
            backgroundColor: activeTab === 'about' ? 'var(--color-primary)' : 'transparent',
            color: activeTab === 'about' ? 'white' : '#333',
            cursor: 'pointer',
            fontWeight: '600',
            borderRadius: '8px 8px 0 0',
            marginBottom: '-2px',
            borderBottom: activeTab === 'about' ? '2px solid var(--color-primary)' : '2px solid transparent'
          }}
        >
          ℹ️ About Page
        </button>
      </div>

      {/* Home Page Content */}
      {activeTab === 'home' && (
        <div className="card" style={{ padding: '25px', marginBottom: '20px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--color-brand-text)' }}>
            Home Page Content
          </h2>

          {/* Why Choose Us Section */}
          {renderArrayEditor(
            homeContent.whyChooseUs,
            null,
            editingWhyChooseId,
            setEditingWhyChooseId,
            [
              { key: 'icon', label: 'Icon', type: 'emoji' },
              { key: 'title', label: 'Title', placeholder: 'Feature Title', type: 'text' },
              { key: 'description', label: 'Description', placeholder: 'Feature description...', type: 'textarea', fullWidth: true }
            ],
            { icon: '⭐', title: '', description: '' },
            '"Why Choose Us" Features',
            'whyChooseUs'
          )}

          {/* How It Works Section */}
          {renderArrayEditor(
            homeContent.howItWorks,
            null,
            editingHowItWorksId,
            setEditingHowItWorksId,
            [
              { key: 'step', label: 'Step #', placeholder: '1', type: 'text' },
              { key: 'title', label: 'Title', placeholder: 'Step Title', type: 'text' },
              { key: 'description', label: 'Description', placeholder: 'Step description...', type: 'textarea', fullWidth: true }
            ],
            { step: homeContent.howItWorks.length + 1, title: '', description: '' },
            '"How It Works" Steps',
            'howItWorks'
          )}
        </div>
      )}

      {/* About Page Content */}
      {activeTab === 'about' && (
        <div className="card" style={{ padding: '25px', marginBottom: '20px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--color-brand-text)' }}>
            About Page Content
          </h2>

          {/* Hero Subtitle */}
          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ margin: '0 0 10px', color: 'var(--color-brand-text)' }}>Hero Subtitle</h3>
            <textarea
              value={aboutContent.heroSubtitle}
              onChange={(e) => setAboutContent(prev => ({ ...prev, heroSubtitle: e.target.value }))}
              placeholder="Enter hero subtitle..."
              rows={2}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>

          {/* Mission Statement */}
          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ margin: '0 0 10px', color: 'var(--color-brand-text)' }}>Mission Statement</h3>
            <textarea
              value={aboutContent.missionStatement}
              onChange={(e) => setAboutContent(prev => ({ ...prev, missionStatement: e.target.value }))}
              placeholder="Enter mission statement..."
              rows={4}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>

          {/* Values Section */}
          {renderArrayEditor(
            aboutContent.values,
            null,
            editingValueId,
            setEditingValueId,
            [
              { key: 'icon', label: 'Icon', type: 'emoji' },
              { key: 'title', label: 'Title', placeholder: 'Value Title', type: 'text' },
              { key: 'description', label: 'Description', placeholder: 'Value description...', type: 'textarea', fullWidth: true }
            ],
            { icon: '⭐', title: '', description: '' },
            'Our Values',
            'values'
          )}

          {/* Timeline Section */}
          {renderArrayEditor(
            aboutContent.timeline,
            null,
            editingTimelineId,
            setEditingTimelineId,
            [
              { key: 'year', label: 'Year', placeholder: '2024', type: 'text' },
              { key: 'title', label: 'Title', placeholder: 'Milestone Title', type: 'text' },
              { key: 'description', label: 'Description', placeholder: 'Milestone description...', type: 'textarea', fullWidth: true }
            ],
            { year: new Date().getFullYear().toString(), title: '', description: '' },
            'Company Timeline',
            'timeline'
          )}

          {/* Offerings Section */}
          {renderArrayEditor(
            aboutContent.offerings,
            null,
            editingOfferingId,
            setEditingOfferingId,
            [
              { key: 'icon', label: 'Icon', type: 'emoji' },
              { key: 'title', label: 'Title', placeholder: 'Offering Title', type: 'text' },
              { key: 'description', label: 'Description', placeholder: 'Offering description...', type: 'textarea', fullWidth: true }
            ],
            { icon: '⭐', title: '', description: '' },
            'What We Offer',
            'offerings'
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', padding: '20px 0' }}>
        <button
          onClick={handleCancel}
          className="btn"
          style={{ padding: '12px 24px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
        >
          Cancel
        </button>
        <button
          onClick={() => !demoDisabled && handleSave()}
          disabled={saving || !hasChanges || demoDisabled}
          className="btn btn-primary"
          style={{ padding: '12px 24px', opacity: (saving || !hasChanges || demoDisabled) ? 0.6 : 1, cursor: (saving || !hasChanges || demoDisabled) ? 'not-allowed' : 'pointer' }}
          title={demoDisabled ? 'Disabled in demo mode' : ''}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

export default PageContentSettings;
